import React, { useState, useRef } from 'react';
import CreatableSelect from 'react-select/creatable';
import { components } from 'react-select';
import { Check, ChevronDown } from 'lucide-react'; // Dropdown arrow

interface MultiSelectOption {
  value: string;
  label: string;
}

interface CreatableMultiSelectInputGroupProps {
  label: string;
  name: string;
  value: string[];
  options?: MultiSelectOption[];
  handleChange: (value: string[]) => void;
  error?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}

const CreatableMultiSelectInputGroup: React.FC<CreatableMultiSelectInputGroupProps> = ({
  label,
  name,
  value = [],
  options = [],
  handleChange,
  error,
  className = '',
  disabled = false,
  placeholder = '',
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const selectRef = useRef<any>(null);

  // Map existing values to options format
  const selectedOptions = value.map(val => ({ value: val, label: val }));
  const hasValue = selectedOptions.length > 0;
  const isLabelFloating = hasValue || isFocused || menuOpen;

  // Custom option with tick on selected
  const CustomOption = (props: any) => {
    const { isSelected, label, innerRef, innerProps } = props;
    return (
      <div
        ref={innerRef}
        {...innerProps}
        className={`flex justify-between items-center px-3 py-2 cursor-pointer ${
          isSelected ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-900'
        }`}
      >
        <span>{label}</span>
        {isSelected && <Check className="w-4 h-4 text-blue-600" />}
      </div>
    );
  };

  // Custom dropdown indicator with spin on open
  const DropdownIndicator = (props: any) => (
    <components.DropdownIndicator {...props}>
      <ChevronDown
        className={`w-5 h-5 transition-transform duration-300 ${
          menuOpen ? 'rotate-180' : 'rotate-0'
        }`}
      />
    </components.DropdownIndicator>
  );

  // Handle creating new options
  const handleCreate = (inputValue: string) => {
    if (!inputValue.trim()) return;
    const newOption = { value: inputValue, label: inputValue };
    // Add the new option to value
    handleChange([...value, inputValue]);
  };

  return (
    <div className={`relative mb-6 ${className}`}>
      <div className="relative">
        <CreatableSelect
          ref={selectRef}
          inputId={name}
          name={name}
          isMulti
          hideSelectedOptions={false}
          closeMenuOnSelect={false}
          isSearchable
          value={selectedOptions}
          options={options}
          onChange={(selected) =>
            handleChange((selected as MultiSelectOption[]).map(opt => opt.value))
          }
          onCreateOption={handleCreate}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onMenuOpen={() => setMenuOpen(true)}
          onMenuClose={() => setMenuOpen(false)}
          isDisabled={disabled}
          placeholder={placeholder}
          formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
          components={{
            Option: CustomOption,
            DropdownIndicator,
            IndicatorSeparator: () => null
          }}
          classNames={{
            control: ({ isFocused }) =>
              `creatablemulti-select !rounded-full !pl-5 !pr-3 !border ${
                error
                  ? '!border-red-500'
                  : isFocused
                  ? '!border-blue-500 !shadow-[0_0_0_3px_rgba(59,130,246,0.1)]'
                  : '!border-gray-300'
              } hover:!border-gray-400 ${
                hasValue ? '!pt-0 !pb-0' : '!pt-0 !pb-0'
              } !px-3 ${
                disabled ? '!bg-gray-50 !cursor-not-allowed' : '!bg-white'
              }`,
            valueContainer: () => `exampleid !p-2 ${hasValue ? '!mt-0' : '!mt-0'}`,
            input: () => '!m-0 !p-0',
            placeholder: () => '!text-gray-400 !m-0',
            multiValue: () => '!bg-blue-100 !text-black rounded-half',
            multiValueLabel: () => 'select-label !text-black ',
            multiValueRemove: () => '!text-black hover:!bg-[#1e40af] hover:!text-white !rounded-full',
            indicatorsContainer: () => 'select-arrow !h-14',
            dropdownIndicator: () => '!text-gray-500 hover:!text-gray-700',
            clearIndicator: () => '!text-black-500 hover:!text-gray-700',
            menu: () => '!rounded-xl !shadow-lg !border !border-gray-200 !z-50',
          }}
          menuPortalTarget={typeof window !== 'undefined' ? window.document.body : undefined}
          menuPosition="fixed"
          menuPlacement="auto"
          styles={{
            menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
          }}
        />
        <label
          htmlFor={name}
          className={`absolute left-5 transition-all duration-200 pointer-events-none
            ${isLabelFloating
              ? 'top-0 -translate-y-1/2 text-xs font-medium scale-85 rounded-full bg-white px-2 !text-blue-600'
              : 'top-1/2 -translate-y-1/2 text-base bg-transparent'}
            peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-xs peer-focus:font-medium
            peer-focus:bg-white peer-focus:px-2 peer-focus:scale-85 peer-focus:rounded-full peer-focus:border peer-focus:border-blue-400 peer-focus:text-blue-600
            ${error ? 'peer-focus:text-red-500' : ''}
            ${disabled ? 'text-gray-400' : 'text-gray-500'}
          `}
        >
          {label}
        </label>
      </div>
      {error && (
        <span className="text-xs text-red-500 mt-1 block">{error}</span>
      )}
    </div>
  );
};

export default CreatableMultiSelectInputGroup;
